import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { catchError, Observable, of } from 'rxjs';
import {
  ChannelsBreakdownRequest,
  ChannelsBreakdownResponse,
  ConversationDashboardRequest,
  ConversationDashboardResponse,
  ConversationFiltersRequest,
  ConversationFiltersResponse,
  ConversationStagesRequest,
  ConversationStagesResponse,
  ConversationTotalsRequest,
  ConversationTotalsResponse,
  HourlyTrafficItem,
  InstancesRequest,
  InstancesResponse,
  TranscriptResponseModel,
} from '../core';

@Injectable({ providedIn: 'root' })
export class PortalService extends ApiService {
  constructor(http: HttpClient) {
    super(http, 'Portal');
  }

  public getTranscript(conversationId: string): Observable<TranscriptResponseModel | null> {
    const id = (conversationId || '').trim();

    if (!id) {
      return of(null);
    }

    const url = `${this.route()}/${encodeURIComponent(id)}/transcript`;

    return this.http.get<TranscriptResponseModel>(url).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          return of(null);
        }

        throw error;
      }),
    );
  }

  public getConversationsDashboard(request: ConversationDashboardRequest): Observable<ConversationDashboardResponse> {
    const url = `${this.route()}/conversations/dashboard`;
    const params = this.buildDashboardParams(request);

    return this.http.get<ConversationDashboardResponse>(url, { params });
  }

  public getInstances(request: InstancesRequest = {}): Observable<InstancesResponse> {
    const url = `${this.route()}/instances`;
    let params = new HttpParams();

    const search = PortalService.normalizeParam(request.search);
    if (search) {
      params = params.set('search', search);
    }
    const column = PortalService.normalizeParam(request.column);
    if (column) {
      params = params.set('column', column);
    }
    if (request.ascending != null) {
      params = params.set('ascending', String(request.ascending));
    }
    if (request.page != null) {
      params = params.set('page', String(request.page));
    }
    if (request.show != null) {
      params = params.set('show', String(request.show));
    }

    return this.http.get<InstancesResponse>(url, { params });
  }

  public getConversationFilters(request: ConversationFiltersRequest): Observable<ConversationFiltersResponse> {
    const url = `${this.route()}/conversations/dashboard/filters`;
    const params = this.buildFiltersParams(request);

    return this.http.get<ConversationFiltersResponse>(url, { params });
  }

  public getConversationStages(request: ConversationStagesRequest): Observable<ConversationStagesResponse> {
    const url = `${this.route()}/conversations/dashboard/stages`;
    let params = this.buildFiltersParams(request);

    if (request.show != null) {
      params = params.set('show', String(request.show));
    }

    return this.http.get<ConversationStagesResponse>(url, { params });
  }

  public getChannelsBreakdown(request: ChannelsBreakdownRequest): Observable<ChannelsBreakdownResponse> {
    const url = `${this.route()}/conversations/dashboard/channels-breakdown`;
    const params = this.buildFiltersParams(request);

    return this.http.get<ChannelsBreakdownResponse>(url, { params });
  }
  public getConversationTotals(request: ConversationTotalsRequest): Observable<ConversationTotalsResponse> {
    const url = `${this.route()}/conversations/dashboard/totals`;
    let params = this.buildFiltersParams(request);

    if (request.show != null) {
      params = params.set('show', String(request.show));
    }

    return this.http.get<ConversationTotalsResponse>(url, { params });
  }

  public getHourlyTraffic(tags?: string | null, instanceIds?: string | null): Observable<HourlyTrafficItem[]> {
    const url = `${this.route()}/conversations/dashboard/hourly-traffic`;
    let params = new HttpParams();

    const normalizedTags = PortalService.normalizeParam(tags);
    if (normalizedTags) {
      params = params.set('tags', normalizedTags);
    }
    const normalizedInstanceIds = PortalService.normalizeParam(instanceIds);
    if (normalizedInstanceIds) {
      params = params.set('instanceIds', normalizedInstanceIds);
    }

    return this.http.get<HourlyTrafficItem[]>(url, { params });
  }
  private buildDashboardParams(request: ConversationDashboardRequest): HttpParams {
    let params = this.buildFiltersParams(request);

    const channel = PortalService.normalizeParam(request.channel);
    if (channel) {
      params = params.set('channel', channel);
    }
    if (request.show != null) {
      params = params.set('show', String(request.show));
    }
    if (request.page != null) {
      params = params.set('page', String(request.page));
    }

    return params;
  }

  private buildFiltersParams(request: ConversationFiltersRequest): HttpParams {
    let params = new HttpParams();

    const tags = PortalService.normalizeParam(request.tags);
    if (tags) {
      params = params.set('tags', tags);
    }
    const dateParam = PortalService.normalizeParam(request.dateParam);
    if (dateParam) {
      params = params.set('dateParam', dateParam);
    }
    const startDate = PortalService.normalizeParam(request.startDate);
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    const endDate = PortalService.normalizeParam(request.endDate);
    if (endDate) {
      params = params.set('endDate', endDate);
    }
    const instanceIds = PortalService.normalizeParam(request.instanceIds);
    if (instanceIds) {
      params = params.set('instanceIds', instanceIds);
    }

    return params;
  }

  private static normalizeParam(value: unknown): string | null {
    if (value == null) {
      return null;
    }
    const str = String(value).trim();
    return str.length > 0 ? str : null;
  }
}
